import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import HomeLayout from '../../asset/layout/HomeLayout';
import CustomButton from '../../components/mui/CustomButton';
import SearchBar from '../../components/mui/SearchBar';
import ResponsiveDatePickers from '../../components/mui/PichDate';
import RModal from '../../components/mui/RModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  InputAdornment,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import PauseCircleOutlineOutlinedIcon from '@mui/icons-material/PauseCircleOutlineOutlined';
import FolderOffOutlinedIcon from '@mui/icons-material/FolderOffOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  closeFailedTransactionCase,
  getFailedTransactions,
  getFailedTransactionsAnalytics,
  refundFailedTransaction,
} from '../../actions/failedTransactionsAuth';
import {
  getOrderRetryAttempts,
  retryOrderDelivery,
} from '../../actions/ordersAuth';

const CASE_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'OPEN', label: 'Open' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'CLOSED', label: 'Closed' },
];

const columns = [
  { key: 'txn', label: 'Transaction', minWidth: 150 },
  { key: 'customer', label: 'Customer', minWidth: 180 },
  { key: 'amount', label: 'Amount', minWidth: 100 },
  { key: 'charge', label: 'Charge', minWidth: 90 },
  { key: 'payment', label: 'Payment', minWidth: 110 },
  { key: 'razorpay', label: 'Razorpay', minWidth: 150 },
  { key: 'txnStatus', label: 'Txn status', minWidth: 110 },
  { key: 'caseStatus', label: 'Case', minWidth: 100 },
  { key: 'created', label: 'Created', minWidth: 140 },
  { key: 'actions', label: 'Actions', minWidth: 210 },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '24px',
      border: '1px solid',
      borderColor: alpha('#11409B', 0.08),
      bgcolor: '#fff',
      boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const StatCard = ({ icon: Icon, label, value, accent, gradient, active, onClick, hint }) => (
  <PanelCard
    sx={{
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
      borderColor: active ? alpha(accent, 0.45) : alpha('#11409B', 0.08),
      boxShadow: active
        ? `0 10px 36px ${alpha(accent, 0.22)}`
        : '0 4px 32px rgba(15, 23, 42, 0.06)',
      '&:hover': onClick
        ? {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 36px ${alpha(accent, 0.2)}`,
          }
        : undefined,
    }}
  >
    <Box
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{ p: 2.25 }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.75}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '14px',
            background: gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: `0 8px 20px ${alpha(accent, 0.28)}`,
          }}
        >
          <Icon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#64748b',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontSize: '0.68rem',
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.45rem', md: '1.65rem' },
              color: '#0f172a',
              letterSpacing: '-0.03em',
              lineHeight: 1.15,
              mt: 0.25,
              wordBreak: 'break-word',
            }}
          >
            {value ?? '—'}
          </Typography>
          {hint ? (
            <Typography sx={{ mt: 0.35, fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>
              {hint}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Box>
  </PanelCard>
);

const getCaseStatusStyles = (status) => {
  const value = String(status || '').toUpperCase();
  if (value === 'OPEN') return { color: '#2563eb', bg: alpha('#2563eb', 0.12) };
  if (value === 'REFUNDED') return { color: '#16a34a', bg: alpha('#16a34a', 0.12) };
  if (value === 'CLOSED') return { color: '#64748b', bg: alpha('#64748b', 0.12) };
  return { color: '#64748b', bg: alpha('#64748b', 0.12) };
};

const CaseStatusChip = ({ status }) => {
  const styles = getCaseStatusStyles(status);
  return (
    <Chip
      label={status || '—'}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: '0.7rem',
        height: 26,
        color: styles.color,
        bgcolor: styles.bg,
        border: `1px solid ${alpha(styles.color, 0.2)}`,
      }}
    />
  );
};

const getPaymentTypeStyles = (paymentType) => {
  const value = String(paymentType || '').toUpperCase();
  if (value === 'WALLET') return { color: '#4f46e5', bg: alpha('#4f46e5', 0.12) };
  if (value === 'RAZORPAY') return { color: '#2563eb', bg: alpha('#2563eb', 0.12) };
  if (value === 'REWARD') return { color: '#7c3aed', bg: alpha('#7c3aed', 0.12) };
  if (value === 'CASHFREE') return { color: '#0891b2', bg: alpha('#0891b2', 0.12) };
  return { color: '#11409B', bg: alpha('#11409B', 0.08) };
};

const PaymentTypeChip = ({ paymentType }) => {
  const styles = getPaymentTypeStyles(paymentType);
  return (
    <Chip
      label={paymentType || '—'}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: '0.7rem',
        height: 26,
        color: styles.color,
        bgcolor: styles.bg,
        border: `1px solid ${alpha(styles.color, 0.2)}`,
      }}
    />
  );
};

const getTxnStatusStyles = (status) => {
  const value = String(status || '').toUpperCase();
  if (value === 'SUCCESS') return { color: '#16a34a', bg: alpha('#16a34a', 0.12) };
  if (value === 'FAILED' || value === 'EXPIRED') {
    return { color: '#dc2626', bg: alpha('#dc2626', 0.12) };
  }
  if (value === 'PENDING') return { color: '#d97706', bg: alpha('#d97706', 0.12) };
  return { color: '#64748b', bg: alpha('#64748b', 0.12) };
};

const TxnStatusChip = ({ status }) => {
  const styles = getTxnStatusStyles(status);
  return (
    <Chip
      label={status || '—'}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: '0.7rem',
        height: 26,
        color: styles.color,
        bgcolor: styles.bg,
        border: `1px solid ${alpha(styles.color, 0.2)}`,
      }}
    />
  );
};

const CopyableId = ({ value, maxWidth = 150 }) => {
  if (!value) {
    return (
      <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
        —
      </Typography>
    );
  }

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(value));
      enqueueSnackbar('Copied to clipboard', { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not copy', { variant: 'error' });
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ maxWidth }}>
      <Tooltip title={value} placement="top-start">
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.75rem',
            color: '#475569',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {value}
        </Typography>
      </Tooltip>
      <IconButton
        size="small"
        onClick={handleCopy}
        aria-label={`Copy ${value}`}
        sx={{
          p: 0.35,
          color: '#94a3b8',
          '&:hover': { color: '#11409B', bgcolor: alpha('#11409B', 0.06) },
        }}
      >
        <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Stack>
  );
};

const getPaymentCharge = (row) =>
  row?.paymentCharge ??
  (Array.isArray(row?.orders) ? row.orders[0]?.paymentCharge : undefined);

const getRefundBreakdown = (row, customFee) => {
  const amount = Number(row?.amount) || 0;
  const charge =
    customFee !== undefined && customFee !== '' && !isNaN(Number(customFee))
      ? Number(customFee)
      : (Number(getPaymentCharge(row)) || 0);
  const netRefund = Math.max(amount - charge, 0);
  return { amount, charge, netRefund };
};

const formatAmount = (value) => {
  if (value == null || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return `₹${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = dayjs(value);
  return date.isValid() ? date.format('DD MMM YYYY, hh:mm A') : '—';
};

const getInitials = (name) => {
  if (!name || name === '—') return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const RefundSummary = ({ row, feeDeduction }) => {
  const { amount, charge, netRefund } = getRefundBreakdown(row, feeDeduction);

  return (
    <Box
      sx={{
        p: 2,
        mb: 2.5,
        borderRadius: '16px',
        background: `linear-gradient(135deg, ${alpha('#16a34a', 0.08)} 0%, ${alpha('#11409B', 0.04)} 100%)`,
        border: `1px solid ${alpha('#16a34a', 0.2)}`,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: '#64748b',
          }}
        >
          Refund calculation
        </Typography>
        <Chip
          size="small"
          label={`Final Refund: ${formatAmount(netRefund)}`}
          sx={{
            height: 22,
            fontWeight: 800,
            fontSize: '0.72rem',
            bgcolor: alpha('#16a34a', 0.15),
            color: '#16a34a',
            border: `1px solid ${alpha('#16a34a', 0.3)}`,
          }}
        />
      </Stack>

      {/* Pill summary showing: Transaction Amount: ₹1,000 | Fee Deduction: ₹20 | Final Refund: ₹980 */}
      <Box
        sx={{
          p: 1.25,
          mb: 1.5,
          borderRadius: '10px',
          bgcolor: '#fff',
          border: '1px solid rgba(0,0,0,0.06)',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.8rem',
            fontWeight: 700,
            color: '#334155',
          }}
        >
          Transaction Amount: <span style={{ color: '#0f172a' }}>{formatAmount(amount)}</span>
          {' | '}Fee Deduction: <span style={{ color: '#dc2626' }}>{formatAmount(charge)}</span>
          {' | '}Final Refund: <span style={{ color: '#16a34a' }}>{formatAmount(netRefund)}</span>
        </Typography>
      </Box>

      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>
            Transaction amount
          </Typography>
          <Typography sx={{ fontWeight: 600, color: '#0f172a' }}>
            {formatAmount(amount)}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>
            Razorpay fee deduction
          </Typography>
          <Typography sx={{ fontWeight: 600, color: charge > 0 ? '#dc2626' : '#64748b' }}>
            {charge > 0 ? `− ${formatAmount(charge)}` : '₹0'}
          </Typography>
        </Stack>
        <Box sx={{ borderTop: `1px dashed ${alpha('#16a34a', 0.25)}`, pt: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
              Final refund amount
            </Typography>
            <Typography sx={{ fontWeight: 800, color: '#16a34a', fontSize: '1.25rem' }}>
              {formatAmount(netRefund)}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

const extractList = (response) => {
  const payload = response?.data?.data ?? response?.data ?? response ?? {};
  const content =
    payload?.content ??
    payload?.data?.content ??
    payload?.items ??
    (Array.isArray(payload) ? payload : []);
  const total =
    payload?.totalElements ??
    payload?.total ??
    payload?.data?.totalElements ??
    (Array.isArray(content) ? content.length : 0);

  return {
    list: Array.isArray(content) ? content : [],
    total: Number(total) || 0,
  };
};

const isExternalPaymentId = (value) => {
  const id = String(value || '');
  return id.startsWith('pay_') || id.startsWith('order_');
};

const getCaseActionId = (row) => {
  const candidates = [
    row?._id,
    row?.id,
    row?.caseId,
    row?.failedTransactionId,
    row?.transaction?._id,
    row?.transaction?.id,
  ];

  for (const candidate of candidates) {
    if (candidate != null && candidate !== '' && !isExternalPaymentId(candidate)) {
      return candidate;
    }
  }

  return null;
};

const getDisplayTransactionId = (row) => {
  if (row?.transactionId && !isExternalPaymentId(row.transactionId)) {
    return row.transactionId;
  }
  return getCaseActionId(row) || row?.transactionId || '—';
};

const getRetryOrderId = (row) => {
  if (row?.orderId) return row.orderId;
  const firstOrder = Array.isArray(row?.orders) ? row.orders[0] : null;
  return firstOrder?.id || firstOrder?._id || null;
};

const getRetryTransactionId = (row) => getCaseActionId(row);

const extractAttempts = (response) => {
  const payload = response?.data?.data ?? response?.data ?? response ?? [];
  return Array.isArray(payload) ? payload : [];
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    height: '2.75rem',
    borderRadius: '12px',
    bgcolor: '#fff',
    '& fieldset': { borderColor: alpha('#11409B', 0.2) },
    '&:hover fieldset': { borderColor: '#11409B' },
  },
};

const RefundManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [caseStatusFilter, setCaseStatusFilter] = useState('');
  const [selectedDateFrom, setSelectedDateFrom] = useState(null);
  const [selectedDateTo, setSelectedDateTo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [cases, setCases] = useState([]);
  const [totalCases, setTotalCases] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(20);
  const [analytics, setAnalytics] = useState({
    totalCases: 0,
    openCount: 0,
    refundedCount: 0,
    closedCount: 0,
    refundedAmount: 0,
  });

  const [actionModal, setActionModal] = useState({
    open: false,
    type: null,
    row: null,
    remarks: '',
    feeDeduction: '',
  });
  const [confirmRefundModal, setConfirmRefundModal] = useState({
    open: false,
    row: null,
  });
  const [placeOrderModal, setPlaceOrderModal] = useState({
    open: false,
    row: null,
    username: '',
    password: '',
    attempts: [],
    attemptsLoading: false,
  });
  const [placeOrderLoading, setPlaceOrderLoading] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/');
    }
  }, [navigate]);

  const fetchCases = useCallback(() => {
    setLoading(true);
    const queryPayload = {
      page: Math.max(currentPage - 1, 0),
      size: countPerPage,
    };

    if (searchTerm?.trim()) queryPayload.search = searchTerm.trim();
    if (userIdFilter?.trim()) queryPayload.userId = userIdFilter.trim();
    if (caseStatusFilter) queryPayload.caseStatus = caseStatusFilter;
    if (selectedDateFrom) {
      queryPayload.createdFrom = dayjs(selectedDateFrom).startOf('day').toISOString();
    }
    if (selectedDateTo) {
      queryPayload.createdTo = dayjs(selectedDateTo).endOf('day').toISOString();
    }

    dispatch(
      getFailedTransactions(
        queryPayload,
        (response) => {
          const { list, total } = extractList(response);
          setCases(list);
          setTotalCases(total);
          setLoading(false);
        },
        (err) => {
          console.log('Error fetching failed transactions:', err);
          enqueueSnackbar(
            err?.response?.data?.message || 'Failed to load refund cases',
            { variant: 'error' }
          );
          setCases([]);
          setTotalCases(0);
          setLoading(false);
        }
      )
    );
  }, [
    dispatch,
    currentPage,
    countPerPage,
    searchTerm,
    userIdFilter,
    caseStatusFilter,
    selectedDateFrom,
    selectedDateTo,
  ]);

  const fetchAnalytics = useCallback(() => {
    setAnalyticsLoading(true);
    const queryPayload = {};
    if (selectedDateFrom) {
      queryPayload.createdFrom = dayjs(selectedDateFrom).startOf('day').toISOString();
    }
    if (selectedDateTo) {
      queryPayload.createdTo = dayjs(selectedDateTo).endOf('day').toISOString();
    }

    dispatch(
      getFailedTransactionsAnalytics(
        queryPayload,
        (response) => {
          const payload = response?.data?.data ?? response?.data ?? response ?? {};
          setAnalytics({
            totalCases: Number(payload?.totalCases) || 0,
            openCount: Number(payload?.openCount) || 0,
            refundedCount: Number(payload?.refundedCount) || 0,
            closedCount: Number(payload?.closedCount) || 0,
            refundedAmount: Number(payload?.refundedAmount) || 0,
          });
          setAnalyticsLoading(false);
        },
        (err) => {
          console.log('Error fetching refund analytics:', err);
          setAnalyticsLoading(false);
          enqueueSnackbar(
            err?.response?.data?.message || 'Failed to load refund analytics',
            { variant: 'error' }
          );
        }
      )
    );
  }, [dispatch, selectedDateFrom, selectedDateTo]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const totalPages = Math.ceil(totalCases / countPerPage) || 1;

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setUserIdFilter('');
    setCaseStatusFilter('');
    setSelectedDateFrom(null);
    setSelectedDateTo(null);
    setCurrentPage(1);
  };

  const handleDeleteFilter = (key) => {
    if (key === 'search') setSearchTerm('');
    if (key === 'userId') setUserIdFilter('');
    if (key === 'status') setCaseStatusFilter('');
    if (key === 'from') setSelectedDateFrom(null);
    if (key === 'to') setSelectedDateTo(null);
    setCurrentPage(1);
  };

  const handleStatFilter = (status) => {
    setCaseStatusFilter((prev) => (prev === status ? '' : status));
    setCurrentPage(1);
  };

  const openActionModal = (type, row) => {
    const defaultCharge = Number(getPaymentCharge(row)) || 0;
    setActionModal({
      open: true,
      type,
      row,
      remarks: '',
      feeDeduction: defaultCharge > 0 ? String(defaultCharge) : '0',
    });
  };

  const openRefundConfirmation = (row) => {
    openActionModal('refund', row);
  };

  const closeRefundConfirmation = () => {
    setConfirmRefundModal({
      open: false,
      row: null,
    });
  };

  const handleRefundConfirm = () => {
    const row = confirmRefundModal.row;
    closeRefundConfirmation();
    if (row) {
      openActionModal('refund', row);
    }
  };

  const closeActionModal = () => {
    setActionModal({
      open: false,
      type: null,
      row: null,
      remarks: '',
      feeDeduction: '',
    });
  };

  const openPlaceOrderModal = (row) => {
    const orderId = getRetryOrderId(row);
    const transactionId = getRetryTransactionId(row);

    setPlaceOrderModal({
      open: true,
      row,
      username: '',
      password: '',
      attempts: [],
      attemptsLoading: true,
    });

    const query = {};
    if (orderId) query.orderId = orderId;
    else if (transactionId) query.transactionId = transactionId;

    if (!query.orderId && !query.transactionId) {
      setPlaceOrderModal((prev) => ({
        ...prev,
        attemptsLoading: false,
      }));
      return;
    }

    dispatch(
      getOrderRetryAttempts(
        query,
        (response) => {
          setPlaceOrderModal((prev) => ({
            ...prev,
            attempts: extractAttempts(response),
            attemptsLoading: false,
          }));
        },
        (err) => {
          console.log('Error fetching retry attempts:', err);
          setPlaceOrderModal((prev) => ({
            ...prev,
            attempts: [],
            attemptsLoading: false,
          }));
          enqueueSnackbar(
            err?.response?.data?.message || 'Could not load previous retry attempts',
            { variant: 'warning' }
          );
        }
      )
    );
  };

  const closePlaceOrderModal = () => {
    if (placeOrderLoading) return;
    setPlaceOrderModal({
      open: false,
      row: null,
      username: '',
      password: '',
      attempts: [],
      attemptsLoading: false,
    });
  };

  const handlePlaceOrderConfirm = () => {
    const row = placeOrderModal.row;
    const orderId = getRetryOrderId(row);
    const transactionId = getRetryTransactionId(row);

    if (!orderId || !transactionId) {
      enqueueSnackbar('Order ID or Transaction ID not found', { variant: 'error' });
      return;
    }
    if (!placeOrderModal.username?.trim() || !placeOrderModal.password) {
      enqueueSnackbar('Admin username and password are required', { variant: 'warning' });
      return;
    }

    setPlaceOrderLoading(true);
    dispatch(
      retryOrderDelivery(
        {
          username: placeOrderModal.username.trim(),
          password: placeOrderModal.password,
          transactionId,
          orderId,
        },
        (response) => {
          const payload = response?.data?.data ?? response?.data ?? response ?? {};
          const tryCount = payload?.tryCount;
          const status = payload?.attempt?.status;
          const newOrderId = payload?.newOrder?.id || payload?.newOrder?._id;
          enqueueSnackbar(
            response?.data?.message ||
              (status === 'FAILED'
                ? `Place order attempt #${tryCount || '—'} failed. Check retry history.`
                : `Order placed successfully${newOrderId ? ` (new order: ${newOrderId})` : ''}`),
            { variant: status === 'FAILED' ? 'warning' : 'success' }
          );
          setPlaceOrderLoading(false);
          setPlaceOrderModal({
            open: false,
            row: null,
            username: '',
            password: '',
            attempts: [],
            attemptsLoading: false,
          });
          fetchCases();
          fetchAnalytics();
        },
        (err) => {
          enqueueSnackbar(
            err?.response?.data?.message || 'Failed to place order. Please try again.',
            { variant: 'error' }
          );
          setPlaceOrderLoading(false);
        }
      )
    );
  };

  const handleActionSubmit = () => {
    const transactionId = getCaseActionId(actionModal.row);
    if (!transactionId) {
      enqueueSnackbar('Case ID not found', { variant: 'error' });
      return;
    }

    const amount = Number(actionModal.row?.amount) || 0;
    const rawDeduction = actionModal.feeDeduction;
    const deductionNum =
      rawDeduction !== undefined && rawDeduction !== '' ? Number(rawDeduction) : 0;

    if (actionModal.type === 'refund') {
      if (isNaN(deductionNum) || deductionNum < 0) {
        enqueueSnackbar('Please enter a valid non-negative fee deduction', {
          variant: 'warning',
        });
        return;
      }
      if (deductionNum > amount) {
        enqueueSnackbar(
          `Fee deduction (${formatAmount(
            deductionNum
          )}) cannot exceed the transaction amount (${formatAmount(amount)})`,
          { variant: 'warning' }
        );
        return;
      }
    }

    if (!actionModal.remarks?.trim()) {
      enqueueSnackbar('Remarks are required', { variant: 'warning' });
      return;
    }

    setActionLoading(true);

    const feeDeduction = !isNaN(deductionNum) && deductionNum >= 0 ? deductionNum : 0;
    const finalRefund = Math.max(amount - feeDeduction, 0);

    const body = {
      remarks: actionModal.remarks.trim(),
      ...(actionModal.type === 'refund'
        ? {
            feeDeduction,
            razorpayFee: feeDeduction,
            paymentCharge: feeDeduction,
            refundAmount: finalRefund,
            finalRefundAmount: finalRefund,
            amount: finalRefund,
          }
        : {}),
    };
    const onSuccess = (response) => {
      enqueueSnackbar(
        response?.data?.message ||
          (actionModal.type === 'refund'
            ? 'Refund processed successfully'
            : 'Case closed successfully'),
        { variant: 'success' }
      );
      setActionLoading(false);
      closeActionModal();
      fetchCases();
      fetchAnalytics();
    };
    const onError = (err) => {
      enqueueSnackbar(
        err?.response?.data?.message || 'Action failed. Please try again.',
        { variant: 'error' }
      );
      setActionLoading(false);
    };

    if (actionModal.type === 'refund') {
      dispatch(refundFailedTransaction(transactionId, body, onSuccess, onError));
      return;
    }

    dispatch(closeFailedTransactionCase(transactionId, body, onSuccess, onError));
  };

  const getCaseStatus = (row) => {
    const status = String(row?.caseStatus || '').toUpperCase();
    return status || 'OPEN';
  };

  const isOpenCase = (row) => getCaseStatus(row) === 'OPEN';

  const canPlaceOrder = (row) => {
    const status = getCaseStatus(row);
    return status !== 'REFUNDED' && status !== 'CLOSED';
  };

  const activeFilters = useMemo(() => {
    const chips = [];
    if (searchTerm?.trim()) chips.push({ key: 'search', label: `Search: ${searchTerm.trim()}` });
    if (userIdFilter?.trim()) chips.push({ key: 'userId', label: `User: ${userIdFilter.trim()}` });
    if (caseStatusFilter) chips.push({ key: 'status', label: `Status: ${caseStatusFilter}` });
    if (selectedDateFrom) {
      chips.push({
        key: 'from',
        label: `From: ${dayjs(selectedDateFrom).format('DD MMM YYYY')}`,
      });
    }
    if (selectedDateTo) {
      chips.push({
        key: 'to',
        label: `To: ${dayjs(selectedDateTo).format('DD MMM YYYY')}`,
      });
    }
    return chips;
  }, [searchTerm, userIdFilter, caseStatusFilter, selectedDateFrom, selectedDateTo]);

  const stats = [
    {
      icon: FolderOffOutlinedIcon,
      label: 'Total cases',
      value: analytics.totalCases,
      accent: '#11409B',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #11409B 100%)',
      status: '',
      hint: 'All failed payment cases',
    },
    {
      icon: LockOpenOutlinedIcon,
      label: 'Open',
      value: analytics.openCount,
      accent: '#2563eb',
      gradient: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)',
      status: 'OPEN',
      hint: 'Needs action',
    },
    {
      icon: CheckCircleOutlineOutlinedIcon,
      label: 'Refunded',
      value: analytics.refundedCount,
      accent: '#16a34a',
      gradient: 'linear-gradient(135deg, #4ade80 0%, #16a34a 100%)',
      status: 'REFUNDED',
      hint: 'Wallet credited',
    },
    {
      icon: PauseCircleOutlineOutlinedIcon,
      label: 'Closed',
      value: analytics.closedCount,
      accent: '#64748b',
      gradient: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
      status: 'CLOSED',
      hint: 'No refund issued',
    },
    {
      icon: CurrencyExchangeOutlinedIcon,
      label: 'Refunded amount',
      value: formatAmount(analytics.refundedAmount),
      accent: '#0f766e',
      gradient: 'linear-gradient(135deg, #2dd4bf 0%, #0f766e 100%)',
      hint: 'Total credited to wallets',
    },
  ];

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: '100%',
          background:
            'linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)',
          mx: { xs: -2, md: -3 },
          mt: { xs: -2, md: -3 },
          px: { xs: 2, md: 3 },
          pt: { xs: 2, md: 3 },
          pb: 4,
        }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 2.5, md: 3.5 },
              mb: 3,
              borderRadius: '24px',
              background:
                'linear-gradient(135deg, #0c3483 0%, #11409B 42%, #1e40af 72%, #2563eb 100%)',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(17, 64, 155, 0.32)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -48,
                right: -24,
                width: 200,
                height: 200,
                borderRadius: '50%',
                bgcolor: alpha('#fff', 0.06),
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -60,
                right: 120,
                width: 140,
                height: 140,
                borderRadius: '50%',
                bgcolor: alpha('#fff', 0.04),
              }}
            />

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2.5}
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <Stack spacing={1} sx={{ maxWidth: 620 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '10px',
                      bgcolor: alpha('#fff', 0.14),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CurrencyExchangeOutlinedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, letterSpacing: '0.12em', opacity: 0.88 }}
                  >
                    REFUND MANAGEMENT
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.03em',
                    fontSize: { xs: '1.6rem', md: '2rem' },
                  }}
                >
                  Failed transaction cases
                </Typography>
                <Typography sx={{ opacity: 0.84, lineHeight: 1.55 }}>
                  Review paid-but-undelivered orders, process wallet refunds, retry gift
                  card delivery, or close cases with remarks.
                </Typography>
              </Stack>

              <Box
                sx={{
                  minWidth: { xs: '100%', md: 220 },
                  p: 2,
                  borderRadius: '18px',
                  bgcolor: alpha('#fff', 0.12),
                  border: `1px solid ${alpha('#fff', 0.18)}`,
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    opacity: 0.8,
                  }}
                >
                  Total refunded
                </Typography>
                <Typography
                  sx={{
                    mt: 0.5,
                    fontWeight: 800,
                    fontSize: '1.7rem',
                    letterSpacing: '-0.03em',
                    lineHeight: 1.1,
                  }}
                >
                  {analyticsLoading ? '—' : formatAmount(analytics.refundedAmount)}
                </Typography>
                <Typography sx={{ mt: 0.75, fontSize: '0.8rem', opacity: 0.8 }}>
                  {analyticsLoading
                    ? 'Loading analytics…'
                    : `${analytics.refundedCount} refunded · ${analytics.openCount} open`}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </motion.div>

        <Grid container columns={{ xs: 1, sm: 2, md: 3, lg: 5 }} spacing={2} sx={{ mb: 3 }}>
          {stats.map((stat, index) => (
            <Grid item key={stat.label} xs={1}>
              <motion.div custom={index} initial="hidden" animate="visible" variants={fadeUp}>
                <StatCard
                  {...stat}
                  value={analyticsLoading ? '—' : stat.value}
                  active={
                    !!stat.status && caseStatusFilter === stat.status
                  }
                  onClick={
                    stat.status
                      ? () => handleStatFilter(stat.status)
                      : () => {
                          setCaseStatusFilter('');
                          setCurrentPage(1);
                        }
                  }
                />
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={1.5}
              mb={2}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <FilterListOutlinedIcon sx={{ color: '#11409B', fontSize: 20 }} />
                <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>Filters</Typography>
              </Stack>
              <CustomButton
                size="small"
                variant="text"
                startIcon={<RefreshOutlinedIcon />}
                onClick={() => {
                  fetchCases();
                  fetchAnalytics();
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#11409B',
                }}
              >
                Refresh
              </CustomButton>
            </Stack>

            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={3}>
                <Typography sx={{ mb: 0.75, fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>
                  Search
                </Typography>
                <SearchBar
                  onSearch={handleSearch}
                  searchTerm={searchTerm}
                  height="2.75rem"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Typography sx={{ mb: 0.75, fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>
                  User ID
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Filter by user ID"
                  value={userIdFilter}
                  onChange={(e) => {
                    setUserIdFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Typography sx={{ mb: 0.75, fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>
                  Case status
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={caseStatusFilter}
                    displayEmpty
                    onChange={(e) => {
                      setCaseStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    sx={{
                      height: '2.75rem',
                      borderRadius: '12px',
                      bgcolor: '#fff',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#11409B', 0.2),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#11409B',
                      },
                    }}
                  >
                    {CASE_STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value || 'all'} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Typography sx={{ mb: 0.75, fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>
                  From date
                </Typography>
                <ResponsiveDatePickers
                  selectedDate={selectedDateFrom}
                  setSelectedDate={(d) => {
                    setSelectedDateFrom(d);
                    setCurrentPage(1);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Typography sx={{ mb: 0.75, fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>
                  To date
                </Typography>
                <ResponsiveDatePickers
                  selectedDate={selectedDateTo}
                  setSelectedDate={(d) => {
                    setSelectedDateTo(d);
                    setCurrentPage(1);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <CustomButton
                  variant="outlined"
                  onClick={handleClearFilters}
                  fullWidth
                  sx={{
                    height: '2.75rem',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: alpha('#11409B', 0.25),
                    color: '#11409B',
                    '&:hover': {
                      borderColor: '#11409B',
                      bgcolor: alpha('#11409B', 0.04),
                    },
                  }}
                >
                  Clear
                </CustomButton>
              </Grid>
            </Grid>

            {activeFilters.length > 0 && (
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 2 }}>
                {activeFilters.map((chip) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    size="small"
                    onDelete={() => handleDeleteFilter(chip.key)}
                    sx={{
                      fontWeight: 600,
                      bgcolor: alpha('#11409B', 0.06),
                      color: '#11409B',
                      border: `1px solid ${alpha('#11409B', 0.12)}`,
                    }}
                  />
                ))}
              </Stack>
            )}
          </PanelCard>
        </motion.div>

        <motion.div custom={5} initial="hidden" animate="visible" variants={fadeUp}>
          {loading ? (
            <PanelCard>
              <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
                <CircularProgress sx={{ color: '#11409B' }} />
                <Typography sx={{ mt: 2, color: '#64748b', fontWeight: 500 }}>
                  Loading failed transactions…
                </Typography>
              </Stack>
            </PanelCard>
          ) : cases.length === 0 ? (
            <PanelCard>
              <Stack alignItems="center" sx={{ py: 9, px: 3 }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '20px',
                    bgcolor: alpha('#11409B', 0.06),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <CurrencyExchangeOutlinedIcon sx={{ fontSize: 34, color: '#94a3b8' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                  No cases found
                </Typography>
                <Typography sx={{ color: '#64748b', mt: 1, textAlign: 'center', maxWidth: 420 }}>
                  Try adjusting your search, status, or date filters. Click a stat card above
                  to quickly filter by case status.
                </Typography>
                {activeFilters.length > 0 && (
                  <CustomButton
                    variant="outlined"
                    onClick={handleClearFilters}
                    sx={{
                      mt: 2.5,
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Clear all filters
                  </CustomButton>
                )}
              </Stack>
            </PanelCard>
          ) : (
            <PanelCard>
              <Box
                sx={{
                  px: 2.5,
                  py: 1.75,
                  borderBottom: `1px solid ${alpha('#11409B', 0.06)}`,
                  bgcolor: alpha('#11409B', 0.02),
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                >
                  <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                    Cases
                    <Typography
                      component="span"
                      sx={{ ml: 1, color: '#64748b', fontWeight: 500 }}
                    >
                      ({totalCases})
                    </Typography>
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                    Sticky actions · click IDs to copy
                  </Typography>
                </Stack>
              </Box>

              <TableContainer sx={{ overflowX: 'auto', maxHeight: '68vh' }}>
                <Table stickyHeader sx={{ minWidth: 1180 }}>
                  <TableHead>
                    <TableRow>
                      {columns.map((header) => (
                        <TableCell
                          key={header.key}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            color: '#11409B',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            whiteSpace: 'nowrap',
                            minWidth: header.minWidth,
                            bgcolor: '#f8fafc',
                            borderBottom: `1px solid ${alpha('#11409B', 0.1)}`,
                            ...(header.key === 'actions'
                              ? {
                                  position: 'sticky',
                                  right: 0,
                                  zIndex: 3,
                                  boxShadow: `-8px 0 16px ${alpha('#0f172a', 0.04)}`,
                                }
                              : {}),
                          }}
                        >
                          {header.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cases.map((row, index) => {
                      const transactionId = getDisplayTransactionId(row);
                      const customerName =
                        row?.userName ||
                        row?.user?.name ||
                        row?.user?.username ||
                        '—';
                      const email = row?.userEmail || row?.user?.email || '—';
                      const phone = row?.userPhone || row?.user?.phone || '—';

                      return (
                        <TableRow
                          key={getCaseActionId(row) || transactionId || index}
                          sx={{
                            '&:hover': { bgcolor: alpha('#11409B', 0.025) },
                            '&:hover td:last-of-type': {
                              bgcolor: '#f9fbff',
                            },
                            '& td': { borderColor: alpha('#11409B', 0.06), py: 1.6 },
                          }}
                        >
                          <TableCell>
                            <CopyableId value={transactionId === '—' ? null : transactionId} />
                          </TableCell>

                          <TableCell>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  bgcolor: alpha('#11409B', 0.12),
                                  color: '#11409B',
                                }}
                              >
                                {getInitials(customerName)}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    color: '#0f172a',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.25,
                                  }}
                                >
                                  {customerName}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: '#64748b',
                                    fontSize: '0.72rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 160,
                                  }}
                                >
                                  {email}
                                </Typography>
                                <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                                  {phone}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>
                              {formatAmount(row?.amount)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography sx={{ fontWeight: 600, color: '#64748b' }}>
                              {formatAmount(getPaymentCharge(row))}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <PaymentTypeChip paymentType={row?.paymentType} />
                          </TableCell>

                          <TableCell>
                            <Stack spacing={0.5}>
                              <CopyableId value={row?.razorpayOrderId} maxWidth={140} />
                              <CopyableId value={row?.razorpayPaymentId} maxWidth={140} />
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <TxnStatusChip status={row?.status || row?.transactionStatus} />
                          </TableCell>

                          <TableCell>
                            <CaseStatusChip status={row?.caseStatus} />
                          </TableCell>

                          <TableCell>
                            <Typography
                              sx={{ color: '#64748b', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                            >
                              {formatDateTime(row?.createdAt)}
                            </Typography>
                          </TableCell>

                          <TableCell
                            sx={{
                              position: 'sticky',
                              right: 0,
                              bgcolor: '#fff',
                              zIndex: 1,
                              boxShadow: `-8px 0 16px ${alpha('#0f172a', 0.03)}`,
                            }}
                          >
                            {isOpenCase(row) ? (
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                {canPlaceOrder(row) && (
                                  <Tooltip title="Retry gift card delivery">
                                    <CustomButton
                                      size="small"
                                      variant="contained"
                                      startIcon={<ReplayOutlinedIcon sx={{ fontSize: 16 }} />}
                                      onClick={() => openPlaceOrderModal(row)}
                                      sx={{
                                        minWidth: 0,
                                        px: 1.25,
                                        py: 0.55,
                                        borderRadius: '10px',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: '0.72rem',
                                        bgcolor: '#11409B',
                                        boxShadow: 'none',
                                        '&:hover': { bgcolor: '#0c3483' },
                                      }}
                                    >
                                      Retry
                                    </CustomButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Process refund to wallet">
                                  <CustomButton
                                    size="small"
                                    variant="contained"
                                    startIcon={
                                      <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 15 }} />
                                    }
                                    onClick={() => openRefundConfirmation(row)}
                                    sx={{
                                      minWidth: 0,
                                      px: 1.25,
                                      py: 0.55,
                                      borderRadius: '10px',
                                      textTransform: 'none',
                                      fontWeight: 700,
                                      fontSize: '0.72rem',
                                      bgcolor: '#16a34a',
                                      boxShadow: 'none',
                                      '&:hover': { bgcolor: '#15803d' },
                                    }}
                                  >
                                    Refund
                                  </CustomButton>
                                </Tooltip>
                                <Tooltip title="Close without refund">
                                  <IconButton
                                    size="small"
                                    onClick={() => openActionModal('close', row)}
                                    sx={{
                                      border: `1px solid ${alpha('#64748b', 0.25)}`,
                                      borderRadius: '10px',
                                      color: '#64748b',
                                      '&:hover': {
                                        bgcolor: alpha('#64748b', 0.08),
                                        borderColor: '#64748b',
                                      },
                                    }}
                                  >
                                    <CloseOutlinedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            ) : (
                              <Chip
                                size="small"
                                label={getCaseStatus(row)}
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '0.68rem',
                                  color: getCaseStatusStyles(getCaseStatus(row)).color,
                                  bgcolor: getCaseStatusStyles(getCaseStatus(row)).bg,
                                }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </PanelCard>
          )}
        </motion.div>

        {!loading && totalCases > 0 && (
          <motion.div custom={6} initial="hidden" animate="visible" variants={fadeUp}>
            <PanelCard sx={{ mt: 3, p: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography sx={{ color: '#64748b', fontWeight: 500 }}>
                  Showing {(currentPage - 1) * countPerPage + 1}–
                  {Math.min(currentPage * countPerPage, totalCases)} of {totalCases}
                </Typography>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems="center"
                  spacing={2}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Rows per page
                  </Typography>
                  <Box
                    component="select"
                    value={countPerPage}
                    onChange={(e) => {
                      setCountPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    sx={{
                      border: `1px solid ${alpha('#11409B', 0.2)}`,
                      borderRadius: '10px',
                      px: 1.5,
                      py: 0.75,
                      fontSize: '0.875rem',
                      bgcolor: '#fff',
                      outline: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </Box>

                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                    <IconButton
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                      sx={{
                        border: '1px solid',
                        borderColor: alpha('#11409B', 0.15),
                        borderRadius: '12px',
                        '&:hover': { bgcolor: alpha('#11409B', 0.06) },
                      }}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <Typography sx={{ fontWeight: 700, minWidth: 52, textAlign: 'center' }}>
                      {currentPage} / {totalPages}
                    </Typography>
                    <IconButton
                      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                      disabled={currentPage >= totalPages}
                      aria-label="Next page"
                      sx={{
                        border: '1px solid',
                        borderColor: alpha('#11409B', 0.15),
                        borderRadius: '12px',
                        '&:hover': { bgcolor: alpha('#11409B', 0.06) },
                      }}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </PanelCard>
          </motion.div>
        )}
      </Box>

      <RModal open={actionModal.open} handleClose={closeActionModal} width={520}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
          {actionModal.type === 'refund' ? 'Process refund' : 'Close case'}
        </Typography>
        <Typography sx={{ color: '#64748b', mb: 2.5, fontSize: '0.9rem' }}>
          {actionModal.type === 'refund'
            ? 'Issue a refund for this failed transaction. Enter a custom Razorpay fee deduction below; the final refund amount is calculated automatically.'
            : 'Close this case without issuing a refund. Remarks are required.'}
        </Typography>

        {actionModal.type === 'refund' && actionModal.row && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Custom Razorpay Fee Deduction"
              value={actionModal.feeDeduction}
              onChange={(e) =>
                setActionModal((prev) => ({
                  ...prev,
                  feeDeduction: e.target.value,
                }))
              }
              placeholder="0"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 700, color: '#64748b' }}>₹</Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: 'any' }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
              }}
              helperText="Enter Razorpay fee amount to deduct. Final refund will update automatically."
            />

            {/* Quick Presets */}
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                size="small"
                label="No deduction (₹0)"
                onClick={() =>
                  setActionModal((prev) => ({ ...prev, feeDeduction: '0' }))
                }
                clickable
                sx={{ fontSize: '0.72rem', fontWeight: 600 }}
              />
              {(() => {
                const defaultCharge = Number(getPaymentCharge(actionModal.row)) || 0;
                return defaultCharge > 0 ? (
                  <Chip
                    size="small"
                    label={`Recorded charge (${formatAmount(defaultCharge)})`}
                    onClick={() =>
                      setActionModal((prev) => ({
                        ...prev,
                        feeDeduction: String(defaultCharge),
                      }))
                    }
                    clickable
                    sx={{ fontSize: '0.72rem', fontWeight: 600 }}
                  />
                ) : null;
              })()}
              {(() => {
                const amt = Number(actionModal.row?.amount) || 0;
                const twoPercent = Math.round(amt * 0.02);
                return twoPercent > 0 ? (
                  <Chip
                    size="small"
                    label={`Standard 2% (${formatAmount(twoPercent)})`}
                    onClick={() =>
                      setActionModal((prev) => ({
                        ...prev,
                        feeDeduction: String(twoPercent),
                      }))
                    }
                    clickable
                    sx={{ fontSize: '0.72rem', fontWeight: 600 }}
                  />
                ) : null;
              })()}
            </Stack>
          </Box>
        )}

        {actionModal.type === 'refund' && actionModal.row && (
          <RefundSummary
            row={actionModal.row}
            feeDeduction={actionModal.feeDeduction}
          />
        )}

        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Remarks"
          value={actionModal.remarks}
          onChange={(e) =>
            setActionModal((prev) => ({ ...prev, remarks: e.target.value }))
          }
          placeholder={
            actionModal.type === 'refund'
              ? 'Customer requested refund'
              : 'Duplicate failure, no refund needed'
          }
          sx={{
            mt: 1,
            '& .MuiOutlinedInput-root': { borderRadius: '14px' },
          }}
        />

        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={1.5}
          justifyContent="flex-end"
          sx={{ mt: 3 }}
        >
          <CustomButton
            variant="outlined"
            onClick={closeActionModal}
            disabled={actionLoading}
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </CustomButton>
          <CustomButton
            variant="contained"
            onClick={handleActionSubmit}
            disabled={actionLoading}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: actionModal.type === 'refund' ? '#16a34a' : '#11409B',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: actionModal.type === 'refund' ? '#15803d' : '#0c3483',
              },
            }}
          >
            {actionLoading
              ? 'Processing…'
              : actionModal.type === 'refund'
              ? 'Confirm refund'
              : 'Confirm close'}
          </CustomButton>
        </Stack>
      </RModal>

      <ConfirmationModal
        open={confirmRefundModal.open}
        handleClose={closeRefundConfirmation}
        handleConfirm={handleRefundConfirm}
        title="Confirm refund"
        message={
          confirmRefundModal.row
            ? (() => {
                const row = confirmRefundModal.row;
                const { amount, charge, netRefund } = getRefundBreakdown(row);
                const customerName =
                  row?.userName ||
                  row?.user?.name ||
                  row?.user?.username ||
                  'this customer';
                return `Are you sure you want to refund ${formatAmount(
                  netRefund
                )} (after deducting ${formatAmount(charge)} charges from ${formatAmount(
                  amount
                )}) for ${customerName}? Transaction ID: ${
                  getDisplayTransactionId(row) || '—'
                }`;
              })()
            : 'Are you sure you want to process this refund?'
        }
        confirmLabel="Yes, proceed to refund"
      />

      <RModal
        open={placeOrderModal.open}
        handleClose={closePlaceOrderModal}
        width={540}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
          Retry / Place order
        </Typography>
        <Typography sx={{ color: '#64748b', mb: 2.5, fontSize: '0.9rem' }}>
          Re-place this paid order to retry gift card delivery. No new payment will be
          charged. Confirm with your admin credentials to continue.
        </Typography>

        {placeOrderModal.row && (
          <Box
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: '16px',
              bgcolor: alpha('#11409B', 0.04),
              border: `1px solid ${alpha('#11409B', 0.12)}`,
            }}
          >
            <Stack spacing={1.1}>
              {[
                [
                  'Customer',
                  placeOrderModal.row?.userName ||
                    placeOrderModal.row?.user?.name ||
                    '—',
                ],
                ['Transaction ID', getDisplayTransactionId(placeOrderModal.row)],
                ['Order ID', getRetryOrderId(placeOrderModal.row) || '—'],
                ['Amount', formatAmount(placeOrderModal.row?.amount)],
              ].map(([label, value]) => (
                <Stack key={label} direction="row" justifyContent="space-between" spacing={2}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                    {label}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: '#0f172a',
                      fontFamily:
                        label.includes('ID')
                          ? 'ui-monospace, SFMono-Regular, Menlo, monospace'
                          : 'inherit',
                      fontSize: label.includes('ID') ? '0.8rem' : '0.9rem',
                      textAlign: 'right',
                      wordBreak: 'break-all',
                    }}
                  >
                    {value}
                  </Typography>
                </Stack>
              ))}
              <Divider sx={{ borderColor: alpha('#11409B', 0.08) }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                  Previous attempts
                </Typography>
                {placeOrderModal.attemptsLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <Typography sx={{ fontWeight: 800, color: '#11409B' }}>
                    {placeOrderModal.attempts.length}
                    {placeOrderModal.attempts.length > 0
                      ? ` (next #${placeOrderModal.attempts.length + 1})`
                      : ''}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Box>
        )}

        {!placeOrderModal.attemptsLoading && placeOrderModal.attempts.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#64748b',
                mb: 1,
              }}
            >
              Retry history
            </Typography>
            <Stack spacing={0.75} sx={{ maxHeight: 160, overflowY: 'auto' }}>
              {placeOrderModal.attempts.map((attempt) => (
                <Stack
                  key={attempt?.id || `${attempt?.tryCount}-${attempt?.createdAt}`}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: '10px',
                    bgcolor: alpha('#0f172a', 0.03),
                  }}
                >
                  <Typography sx={{ fontSize: '0.8rem', color: '#334155' }}>
                    Attempt #{attempt?.tryCount ?? '—'}
                    {attempt?.newOrderId ? ` → ${attempt.newOrderId}` : ''}
                  </Typography>
                  <Chip
                    size="small"
                    label={attempt?.status || '—'}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      height: 22,
                      color:
                        String(attempt?.status).toUpperCase() === 'SUCCESS'
                          ? '#16a34a'
                          : '#dc2626',
                      bgcolor:
                        String(attempt?.status).toUpperCase() === 'SUCCESS'
                          ? alpha('#16a34a', 0.12)
                          : alpha('#dc2626', 0.12),
                    }}
                  />
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Admin username or email"
            value={placeOrderModal.username}
            onChange={(e) =>
              setPlaceOrderModal((prev) => ({ ...prev, username: e.target.value }))
            }
            disabled={placeOrderLoading}
            autoComplete="username"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
          <TextField
            fullWidth
            type="password"
            label="Admin password"
            value={placeOrderModal.password}
            onChange={(e) =>
              setPlaceOrderModal((prev) => ({ ...prev, password: e.target.value }))
            }
            disabled={placeOrderLoading}
            autoComplete="current-password"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </Stack>

        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={1.5}
          justifyContent="flex-end"
          sx={{ mt: 3 }}
        >
          <CustomButton
            variant="outlined"
            onClick={closePlaceOrderModal}
            disabled={placeOrderLoading}
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </CustomButton>
          <CustomButton
            variant="contained"
            onClick={handlePlaceOrderConfirm}
            disabled={placeOrderLoading || placeOrderModal.attemptsLoading}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#11409B',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0c3483' },
            }}
          >
            {placeOrderLoading ? 'Placing order…' : 'Confirm & Place Order'}
          </CustomButton>
        </Stack>
      </RModal>
    </HomeLayout>
  );
};

export default RefundManagement;
